import * as React from 'react';

import { DescriptionPage_ProductVariant as ProductVariant } from '__generated__/graphql-types';
import { compose } from 'recompose';

import { useLocation, useParams } from 'js/lib/useRouter';

import { redirectClientOrServer } from 'bundles/common/utils/urlUtils';
import Naptime from 'bundles/naptimejs';
import type { InjectedNaptime } from 'bundles/naptimejs';
import { useEnterpriseProductFeatures } from 'bundles/unified-common/contexts/EnterpriseProductFeaturesContext';
import getProductTitle from 'bundles/unified-common/utils/getProductTitle';
import EnterpriseSpecializationEnrollButtonNotConfigurable from 'bundles/unified-description-page-common/components/enroll/enterprise/EnterpriseSpecializationEnrollButtonNotConfigurable';
import LoadingOverlay from 'bundles/unified-description-page-common/components/loading/LoadingOverlay';
import MobileCtaModule from 'bundles/unified-description-page-common/components/modules/mobile-cta/MobileCtaModule';
import FixedPageNavigation from 'bundles/unified-description-page-common/components/modules/page-navigation/FixedPageNavigation';
import NavCoBrandingPartnerLogos from 'bundles/unified-description-page-common/components/modules/page-navigation/NavCoBrandingPartnerLogos';
import NavPartnerLogo from 'bundles/unified-description-page-common/components/modules/page-navigation/NavPartnerLogo';
import SpecializationAndProfessionalCertificatePageSEO from 'bundles/unified-description-page-common/components/seo/SpecializationAndProfessionalCertificatePageSEO';
import PageDataErrorHandler from 'bundles/unified-description-page-common/components/shared/PageDataErrorHandler';
import { GOOGLE_XDP_SLUG_TO_PROF_CERT } from 'bundles/unified-description-page-common/constants/googleCerts';
import { useEnterpriseSpecializationPageData } from 'bundles/unified-description-page-common/contexts/enterprise/EnterpriseBffSpecializationContext';
import { useEnterpriseLearnerFlowOptimization } from 'bundles/unified-description-page-common/contexts/enterprise/EnterpriseLearnerFlowOptimizationContext';
import { useAutoContinueWithSpecialization } from 'bundles/unified-description-page-common/hooks/useContinueWithSpecialization';
import useLogRecentlyViewed from 'bundles/unified-description-page-common/hooks/useRecentlyViewed';
import useSwitchToCurrentProgram from 'bundles/unified-description-page-common/hooks/useSwitchToCurrentProgram';
import EnterpriseSpecializationCitationModuleRenderer from 'bundles/unified-description-page-common/module-renderers/citation/EnterpriseSpecializationCitationModuleRenderer';
import Renderer from 'bundles/unified-description-page-common/pages/enterprise/specialization/EnterpriseSpecializationPageRenderer';
import EventingDataProvider from 'bundles/unified-description-page-common/providers/EventingDataProvider';
import LanguageInfoDialogProvider from 'bundles/unified-description-page-common/providers/LanguageInfoDialogProvider';
import PageInfoProvider from 'bundles/unified-description-page-common/providers/PageInfoProvider';
import PageNavigationProvider from 'bundles/unified-description-page-common/providers/PageNavigationProvider';
import StickyPanelProvider from 'bundles/unified-description-page-common/providers/StickyPanelProvider';
import { ModuleNames } from 'bundles/unified-description-page-common/types/moduleConfig';
import { getCourseProductUrl } from 'bundles/unified-description-page-common/utils';
import {
  SPECIALIZATION_NAV_ITEMS,
  getSpecializationPageNavigationLinks,
} from 'bundles/unified-description-page-common/utils/getPageNavigationLinks';
import getPartnersForSpecialization from 'bundles/unified-description-page-common/utils/getPartnersForSpecialization';
import getProductId from 'bundles/unified-description-page-common/utils/getProductId';
import { getEnterpriseRouteForSpecializationProducts } from 'bundles/unified-description-page-common/utils/getRouteForSpecializationProducts';
import hasCareerOutcomesModule from 'bundles/unified-description-page-common/utils/hasCareerOutcomesModule';
import isSlugDuplicatedGoogleCert from 'bundles/unified-description-page-common/utils/isSlugDuplicatedGoogleCert';

import _t from 'i18n!nls/xdp';

type Props = {
  naptime: InjectedNaptime;
};

const EnterpriseSpecializationPageRootContent = ({ naptime }: Props) => {
  const pageData = useEnterpriseSpecializationPageData();
  const { search, query } = useLocation();
  const { programSlug, productSlug: productSlugInUrl } = useParams();
  const { resourcePath, isLoading: isLoadingAutoContinue } = useAutoContinueWithSpecialization(
    query.isExternal,
    pageData?.id
  );
  const { isEnrolling } = useEnterpriseLearnerFlowOptimization();
  const shouldRedirectToNextItem = !!resourcePath;
  const features = useEnterpriseProductFeatures();
  useSwitchToCurrentProgram();

  useLogRecentlyViewed({
    name: pageData?.name,
    slug: pageData?.slug,
    partners: pageData?.partners,
    id: getProductId('specialization', pageData?.id),
    difficulty: pageData?.difficultyLevel,
    productType: pageData?.productVariant,
  });

  // Redirect to next item in the active course if available
  React.useEffect(() => {
    if (resourcePath) {
      redirectClientOrServer(resourcePath);
    }
  }, [resourcePath]);

  if (!pageData) {
    return null;
  } else if (isSlugDuplicatedGoogleCert(pageData.slug)) {
    // If duplicated Google Cert slug, redirect to canonical source.
    redirectClientOrServer(
      getEnterpriseRouteForSpecializationProducts(
        ProductVariant.ProfessionalCertificate,
        GOOGLE_XDP_SLUG_TO_PROF_CERT[pageData.slug as keyof typeof GOOGLE_XDP_SLUG_TO_PROF_CERT],
        programSlug,
        search
      )
    );
  } else if (pageData.productVariant === ProductVariant.GoogleCertificate) {
    return <PageDataErrorHandler pathname={location.pathname} />;
  } else if (pageData.productVariant !== ProductVariant.StandardSpecialization || productSlugInUrl !== pageData.slug) {
    redirectClientOrServer(
      getEnterpriseRouteForSpecializationProducts(pageData.productVariant, pageData.slug, programSlug, search)
    );
  }

  const productTitle = getProductTitle(pageData.name, pageData.productVariant);
  const partnersToDisplay = getPartnersForSpecialization(pageData.partners, !!pageData.coBrandingEnabled);

  const pageConfig = {
    modules: [
      { name: ModuleNames.Breadcrumbs },
      {
        name: ModuleNames.ProductOverview,
      },
      { name: ModuleNames.ThinBanner },
      {
        name: ModuleNames.CareerOutcomes,
      },
      {
        name: ModuleNames.CareerCertificate,
      },
      {
        name: ModuleNames.LearnerIncentive,
      },
      {
        name: ModuleNames.Syllabus,
      },
      {
        name: ModuleNames.EligibleDegrees,
      },
      {
        name: ModuleNames.Testimonials,
      },
      {
        name: ModuleNames.Upsell,
      },
    ],
  };

  const shouldShowAiDubbingOnXdp = !!pageData.dubbedLanguages?.length;

  const navigationLinks = getSpecializationPageNavigationLinks({
    pageData,
    pageConfig,
  });

  const firstCourseHomeLink =
    pageData.courses?.[0]?.slug && `${getCourseProductUrl(pageData.courses?.[0]?.slug, false)}/home`;

  const enrollButtonSmall = (
    <EnterpriseSpecializationEnrollButtonNotConfigurable
      productId={pageData.id}
      firstCourseHomeLink={firstCourseHomeLink}
      firstCourseId={pageData.courses?.[0]?.id}
      size="small"
    />
  );

  // Redirection would be in progress if resourcePath is available
  // Show a loader if redirection is in progress with optimised learner flow enabled
  if (features?.enableOptimizedLearnerFlow && (isLoadingAutoContinue || shouldRedirectToNextItem || isEnrolling)) {
    // eslint-disable-next-line no-restricted-syntax
    return <LoadingOverlay label={_t('Loading the course...')} />;
  }

  return (
    <PageInfoProvider
      programSlug={programSlug}
      navigationIdMap={SPECIALIZATION_NAV_ITEMS}
      productSlug={pageData.slug}
      hasDubbedLanguages={shouldShowAiDubbingOnXdp}
    >
      <EventingDataProvider productId={pageData.id} productType="s12n">
        <PageNavigationProvider navigationLinks={navigationLinks}>
          <StickyPanelProvider>
            <LanguageInfoDialogProvider
              languages={{
                primaryLanguages: pageData.primaryLanguages,
                subtitleLanguages: pageData.subtitleLanguages ?? [],
                translatedLanguages: pageData.translatedLanguages ?? [],
                dubbedLanguages: shouldShowAiDubbingOnXdp ? pageData.dubbedLanguages : null,
              }}
            >
              <FixedPageNavigation
                productTitle={productTitle}
                navigationLinks={navigationLinks}
                pageCta={enrollButtonSmall}
                partnerLogo={
                  partnersToDisplay?.length === 2 ? (
                    <NavCoBrandingPartnerLogos
                      // @ts-expect-error URL: string
                      leftImageSrc={partnersToDisplay[0].squareLogo}
                      leftPartnerName={partnersToDisplay[0].name}
                      // @ts-expect-error URL: string
                      rightImageSrc={partnersToDisplay[1].squareLogo}
                      rightPartnerName={partnersToDisplay[1].name}
                    />
                  ) : (
                    <NavPartnerLogo
                      // @ts-expect-error URL: string
                      imageSrc={partnersToDisplay?.[0].squareLogo}
                      partnerName={partnersToDisplay?.[0].name}
                    />
                  )
                }
              />
              <Renderer pageData={pageData} pageConfig={pageConfig} naptime={naptime} />
              {hasCareerOutcomesModule(pageConfig) && (
                <EnterpriseSpecializationCitationModuleRenderer pageData={pageData} />
              )}
              <MobileCtaModule pageCta={enrollButtonSmall} />
              <SpecializationAndProfessionalCertificatePageSEO pageData={pageData} disableCrawlerIndexing />
            </LanguageInfoDialogProvider>
          </StickyPanelProvider>
        </PageNavigationProvider>
      </EventingDataProvider>
    </PageInfoProvider>
  );
};

// Need naptime instance for enterprise save functionality.
export default compose<Props, {}>(Naptime.createContainer(() => ({})))(EnterpriseSpecializationPageRootContent);
